import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  cn,
  Popover,
  PopoverContent,
  PopoverTrigger,
  PseudoInputButton,
  PseudoInputIcon,
  PseudoInputLabel,
} from '@1trading/react';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { optionSearchScore, optionSearchTerms, SEARCHABLE_TRIGGER_CLASS, SearchableOptionContent, type SearchableSelectOption } from '@/components/ui/searchable-select';

interface SearchableMultiSelectProps {
  id?: string;
  value: string[];
  onChange: (value: string[]) => void;
  options: SearchableSelectOption[];
  placeholder: string;
  searchPlaceholder: string;
  empty: string;
  disabled?: boolean;
  'aria-invalid'?: boolean;
  className?: string;
  /** Up to this many picked labels are named on the trigger; more are counted */
  namedLimit?: number;
}

const NAMED_LIMIT = 2;

/**
 * `SearchableSelect` that toggles each option and stays open, so several can be picked in one go. Modal like it, so its
 * list still scrolls inside a dialog.
 */
export const SearchableMultiSelect = ({
  id,
  value,
  onChange,
  options,
  placeholder,
  searchPlaceholder,
  empty,
  disabled,
  'aria-invalid': ariaInvalid,
  className,
  namedLimit = NAMED_LIMIT,
}: SearchableMultiSelectProps) => {
  const [open, setOpen] = useState(false);
  const picked = new Set(value);
  const labels = options.filter((option) => picked.has(option.value)).map((option) => option.label);
  const summary = !labels.length ? placeholder : labels.length <= namedLimit ? labels.join(', ') : `${labels.length} selected`;
  const toggle = (option: string) => onChange(picked.has(option) ? value.filter((item) => item !== option) : [...value, option]);

  return (
    <Popover modal onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild>
        <PseudoInputButton
          aria-invalid={ariaInvalid}
          className={cn(SEARCHABLE_TRIGGER_CLASS, className)}
          disabled={disabled}
          id={id}
          title={labels.length ? labels.join(', ') : undefined}
        >
          <PseudoInputLabel className={cn(!labels.length && 'text-muted-foreground')}>{summary}</PseudoInputLabel>
          <PseudoInputIcon>
            <ChevronDown />
          </PseudoInputIcon>
        </PseudoInputButton>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto min-w-[var(--radix-popover-trigger-width)] p-0">
        <Command filter={optionSearchScore}>
          <CommandInput className="text-base" placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty className="py-3 text-center text-base">{empty}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem className="text-base" key={option.value} keywords={optionSearchTerms(option)} onSelect={() => toggle(option.value)} value={option.value}>
                  <SearchableOptionContent isSelected={picked.has(option.value)} option={option} />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
SearchableMultiSelect.displayName = 'SearchableMultiSelect';
